/**
 * SSI Snapshot View
 *
 * Read-only replica of the Semi-Structured Interview proforma (Symptoms +
 * Domains + Review notes) as it stood when a Lead clinician took over the
 * module (SLaM / 3 Borough). Data comes from
 * patients/{id}/ssiOverrideArchive/{archiveId}:
 *   - previousSSIModule            → Symptoms / Domains decisions & notes
 *   - previousEmergingFormulation  → Emerging Formulation / Confidential Notes
 *
 * Design choices:
 * - Looks and navigates like the live SSI (side navbar, question panels,
 *   Go to Domains / Go to Review & Synthesis) so clinicians recognise the
 *   same proforma, not a separate summary page.
 * - Nested PatientDataContext injects the archived SSI module and no-ops
 *   all writes — the archive cannot be mutated from this UI.
 * - AI / approved paragraphs are stored in the archive for audit but are
 *   intentionally not shown here; this view is the clinical proforma only.
 * - Exit from Review returns to Patient Overview. Under an active Lead
 *   takeover the live SSI Review hides these personal-note panels, so the
 *   snapshot is the only place they remain visible.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import DictationTextArea from '../../../components/DictationTextArea';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { formatDateTime } from '../../../utils/timestampUtils';
import SSISideNavbar from './components/SSISideNavbar';
import SSISymptomQuestionPanel from './components/SSISymptomQuestionPanel';
import SSIDomainQuestionPanel from './components/SSIDomainQuestionPanel';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';

const STAGE_TITLE = 'Semi-Structured Interview';
const SYMPTOMS_TITLE = 'Symptoms';
const DOMAINS_TITLE = 'Domains of Functional Impairment';
const REVIEW_TITLE = 'Review & Synthesis';

/**
 * Pull questions for a section key ('1' = Symptoms, '2' = Domains) from the
 * snapshot patient model, sorted numerically so order matches the live form.
 */
function getSectionQuestions(patientDataModel, sectionNumber) {
  const section =
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.[
      String(sectionNumber)
    ];
  if (!section?.questions) return [];
  return Object.entries(section.questions)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, question]) => question);
}

/**
 * Amber info strip so it is obvious this is the archived original record,
 * not the Lead's current editable SSI.
 */
function SnapshotMetaBanner({ archive }) {
  if (!archive) return null;

  const originalName =
    archive.ssiClinicianName ||
    archive.ssiClinician?.clinicianName ||
    'Unknown';
  const takenOverBy = archive.archivedByName || 'Lead clinician';
  const completedAt =
    formatDateTime(
      archive.completionMetadata?.firstCompletedAt ||
        archive.completionMetadata?.lastCompletedAt
    ) || '—';
  const archivedAt = formatDateTime(archive.archivedAt) || '—';

  return (
    <div className="mx-8 mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-left">
      <p className="text-sm font-semibold text-amber-900">
        Original Semi-Structured Interview (read-only)
      </p>
      <p className="text-sm text-amber-800 mt-1">
        Snapshot of <strong>{originalName}</strong>&apos;s answers, taken when{' '}
        <strong>{takenOverBy}</strong> took over this module. You can browse
        Symptoms, Domains, and their Review notes exactly as they were at
        takeover; nothing on this page can be edited.
      </p>
      {/* Keep the two timestamps side by side and left-aligned (not stretched
          across a 2-column grid) so "Snapshot taken" sits immediately next to
          "Original completion" rather than floating in the centre. */}
      <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-xs text-amber-800">
        <div>
          <dt className="font-semibold">Original completion</dt>
          <dd>{completedAt}</dd>
        </div>
        <div>
          <dt className="font-semibold">Snapshot taken</dt>
          <dd>{archivedAt}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Symptoms page of the snapshot — same shell as 1_Symptoms.js but always
 * read-only and wired to snapshot routes via proformaNavOverride.
 */
function SnapshotSymptomsSection({
  patientId,
  archiveId,
  patientDataModel,
  informantsData,
  archive,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchPatientData, updatePatientFirestore } = useContext(PatientDataContext);

  const currentSectionNumber = 1;
  const questions = useMemo(
    () => getSectionQuestions(patientDataModel, currentSectionNumber),
    [patientDataModel]
  );

  // Derived synchronously from the archive so the panel never mounts with an
  // empty selections array (which would crash on selections[index].adulthood).
  // Read-only: no local edits, so useState is unnecessary for these fields.
  const selections = useMemo(
    () =>
      questions.map((question) => ({
        adulthood:
          question.symptomPresent_Adulthood !== ''
            ? question.symptomPresent_Adulthood
            : null,
        childhood:
          question.symptomPresent_Childhood !== ''
            ? question.symptomPresent_Childhood
            : null,
      })),
    [questions]
  );
  const settings = useMemo(
    () =>
      questions.map((question) => ({
        adulthood: question.presentInSettings_Adulthood || [],
      })),
    [questions]
  );
  const notes = useMemo(
    () =>
      questions.map((question) => ({
        adulthood: question.clinicianNotes_Adulthood || '',
        childhood: question.clinicianNotes_Childhood || '',
      })),
    [questions]
  );

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const activeRecording = null;
  const textAreaRefs = useRef([]);

  useEffect(() => {
    if (questions.length > 0) {
      textAreaRefs.current = questions.map((_, i) => ({
        adulthood: textAreaRefs.current[i]?.adulthood || React.createRef(),
        childhood: textAreaRefs.current[i]?.childhood || React.createRef(),
      }));
    }
  }, [questions]);

  useEffect(() => {
    if (location.state?.jumpToQuestion !== undefined) {
      const targetIndex = location.state.jumpToQuestion;
      if (targetIndex >= 0 && targetIndex < questions.length) {
        setActiveQuestionIndex(targetIndex);
      }
      window.history.replaceState({}, '');
    }
  }, [location.state, questions.length]);

  const jumpToQuestion = useCallback((index) => {
    setActiveQuestionIndex(index);
    const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
    if (mainContentArea) {
      mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // No-op handlers — canEdit is false on the panel, but keep the same
  // signatures as the live page so the panel does not need special cases.
  const handleSelection = () => {};
  const handleNoteChange = () => {};
  const handleRecording = () => {};
  const handleSettingsChange = () => {};

  const symptomsPath = `/patient/${patientId}/ssi-snapshot/${archiveId}/symptoms`;
  const domainsPath = `/patient/${patientId}/ssi-snapshot/${archiveId}/domains`;
  // Domains forward CTA continues into the archived Review notes (not Overview),
  // so the original Emerging Formulation / Confidential Notes stay reachable.
  const proformaNavOverride = {
    symptomsPath,
    domainsPath,
    afterDomainsPath: `/patient/${patientId}/ssi-snapshot/${archiveId}/review`,
    afterDomainsLabel: 'Go to Review & Synthesis',
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Original SSI Record', `/patient/${patientId}/ssi-snapshot/${archiveId}`],
    [SYMPTOMS_TITLE],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <SnapshotMetaBanner archive={archive} />
      <div className="flex">
        <SSISideNavbar
          questions={questions}
          activeQuestionIndex={activeQuestionIndex}
          selections={selections}
          jumpToQuestion={jumpToQuestion}
          currentSectionTitle={SYMPTOMS_TITLE}
        />
        <div className="flex-grow">
          {questions.length > 0 && (
            <SSISymptomQuestionPanel
              currentStageTitle={STAGE_TITLE}
              currentSectionTitle={SYMPTOMS_TITLE}
              currentSectionNumber={currentSectionNumber}
              questions={questions}
              question={questions[activeQuestionIndex]}
              index={activeQuestionIndex}
              selections={selections}
              notes={notes}
              settings={settings}
              activeRecording={activeRecording}
              handleSelection={handleSelection}
              handleNoteChange={handleNoteChange}
              handleRecording={handleRecording}
              handleSettingsChange={handleSettingsChange}
              jumpToQuestion={jumpToQuestion}
              patientDataModel={patientDataModel}
              fetchPatientData={fetchPatientData}
              updatePatientFirestore={updatePatientFirestore}
              patientId={patientId}
              navigate={navigate}
              textAreaRefs={textAreaRefs}
              informantsData={informantsData}
              canEdit={false}
              proformaNavOverride={proformaNavOverride}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

/**
 * Domains page of the snapshot — same shell as 2_Domains.js, read-only.
 */
function SnapshotDomainsSection({
  patientId,
  archiveId,
  patientDataModel,
  archive,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchPatientData, updatePatientFirestore } = useContext(PatientDataContext);

  const currentSectionNumber = 2;
  const questions = useMemo(
    () => getSectionQuestions(patientDataModel, currentSectionNumber),
    [patientDataModel]
  );

  // Same as Symptoms: derive read-only panel state synchronously from archive.
  const selections = useMemo(
    () =>
      questions.map((question) => ({
        adulthood:
          question.functioningReduced_Adulthood !== ''
            ? question.functioningReduced_Adulthood
            : null,
        childhood:
          question.functioningReduced_Childhood !== ''
            ? question.functioningReduced_Childhood
            : null,
      })),
    [questions]
  );
  const notes = useMemo(
    () =>
      questions.map((question) => ({
        adulthood: question.clinicianNotes_Adulthood || '',
        childhood: question.clinicianNotes_Childhood || '',
      })),
    [questions]
  );

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const activeRecording = null;
  const textAreaRefs = useRef([]);

  useEffect(() => {
    if (questions.length > 0) {
      textAreaRefs.current = questions.map((_, i) => ({
        adulthood: textAreaRefs.current[i]?.adulthood || React.createRef(),
        childhood: textAreaRefs.current[i]?.childhood || React.createRef(),
      }));
    }
  }, [questions]);

  useEffect(() => {
    if (location.state?.jumpToQuestion !== undefined) {
      const targetIndex = location.state.jumpToQuestion;
      if (targetIndex >= 0 && targetIndex < questions.length) {
        setActiveQuestionIndex(targetIndex);
      }
      window.history.replaceState({}, '');
    }
  }, [location.state, questions.length]);

  const jumpToQuestion = useCallback((index) => {
    setActiveQuestionIndex(index);
    const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
    if (mainContentArea) {
      mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleSelection = () => {};
  const handleNoteChange = () => {};
  const handleRecording = () => {};

  const symptomsPath = `/patient/${patientId}/ssi-snapshot/${archiveId}/symptoms`;
  const domainsPath = `/patient/${patientId}/ssi-snapshot/${archiveId}/domains`;
  const proformaNavOverride = {
    symptomsPath,
    domainsPath,
    afterDomainsPath: `/patient/${patientId}/ssi-snapshot/${archiveId}/review`,
    afterDomainsLabel: 'Go to Review & Synthesis',
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Original SSI Record', `/patient/${patientId}/ssi-snapshot/${archiveId}`],
    [DOMAINS_TITLE],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <SnapshotMetaBanner archive={archive} />
      <div className="flex">
        <SSISideNavbar
          questions={questions}
          activeQuestionIndex={activeQuestionIndex}
          selections={selections}
          jumpToQuestion={jumpToQuestion}
          currentSectionTitle={DOMAINS_TITLE}
        />
        <div className="flex-grow">
          {questions.length > 0 && (
            <SSIDomainQuestionPanel
              currentStageTitle={STAGE_TITLE}
              currentSectionTitle={DOMAINS_TITLE}
              currentSectionNumber={currentSectionNumber}
              questions={questions}
              question={questions[activeQuestionIndex]}
              index={activeQuestionIndex}
              selections={selections}
              notes={notes}
              activeRecording={activeRecording}
              handleSelection={handleSelection}
              handleNoteChange={handleNoteChange}
              handleRecording={handleRecording}
              jumpToQuestion={jumpToQuestion}
              patientDataModel={patientDataModel}
              fetchPatientData={fetchPatientData}
              updatePatientFirestore={updatePatientFirestore}
              patientId={patientId}
              navigate={navigate}
              textAreaRefs={textAreaRefs}
              canEdit={false}
              proformaNavOverride={proformaNavOverride}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

/**
 * Snapshot Review & Synthesis — read-only Emerging Formulation and
 * Confidential Notes as captured at takeover. The live SSI Review hides
 * these panels while a Lead override is active, so this is where clinicians
 * (Lead or original SSI) go to see the original clinician's personal notes.
 */
function SnapshotReviewSection({ patientId, archiveId, archive }) {
  const navigate = useNavigate();
  const emerging = archive?.previousEmergingFormulation || {};
  const formulation = emerging.formulation || '';
  const confidentialNotes = emerging.confidentialNotes || '';
  const originalName =
    archive?.ssiClinicianName ||
    archive?.ssiClinician?.clinicianName ||
    'the original SSI clinician';

  const domainsPath = `/patient/${patientId}/ssi-snapshot/${archiveId}/domains`;
  const overviewPath = `/patient/${patientId}`;

  const breadcrumbTuples = [
    ['Patient Overview', overviewPath],
    ['Original SSI Record', `/patient/${patientId}/ssi-snapshot/${archiveId}`],
    [REVIEW_TITLE],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <SnapshotMetaBanner archive={archive} />
      <div className="flex-grow p-8">
        <div className="w-[70%] mx-auto">
          <div className="flex justify-between items-center mb-6">
            <button
              type="button"
              onClick={() => navigate(domainsPath)}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-300"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">Back to Domains</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(overviewPath)}
              className="flex items-center py-2 px-4 rounded-md shadow-md hover:bg-violet-800 bg-violet-900 text-white transition-all duration-300"
            >
              <span className="font-bold">Back to Patient Overview</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          </div>

          <h2 className="text-2xl font-bold mb-2">{REVIEW_TITLE}</h2>
          <p className="text-sm text-amber-900/90 mb-6">
            Personal notes recorded by <strong>{originalName}</strong> before
            takeover. These are not editable here, and they are not shown on
            the Lead&apos;s live SSI Review page.
          </p>

          <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md flex flex-col">
            <h3 className="font-bold text-lg mb-2">Emerging Formulation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Working hypothesis captured at the original Semi-Structured
              Interview appointment.
            </p>
            <DictationTextArea
              value={formulation}
              onChange={() => {}}
              onBlur={() => {}}
              canEdit={false}
              ariaLabel="Emerging formulation notes"
            />
            <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
              Not included in final report
            </p>
          </div>

          <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md mt-6 flex flex-col">
            <h3 className="font-bold text-lg mb-2">Confidential Notes</h3>
            <p className="text-sm text-gray-600 mb-4">
              Confidential or sensitive observations recorded by{' '}
              <strong>{originalName}</strong>.
            </p>
            <DictationTextArea
              value={confidentialNotes}
              onChange={() => {}}
              onBlur={() => {}}
              canEdit={false}
              ariaLabel="Confidential case notes"
            />
            <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
              Not included in final report
            </p>
          </div>

          <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(overviewPath)}
              className="flex items-center py-2 px-4 rounded-md shadow-md hover:bg-violet-800 bg-violet-900 text-white transition-all duration-300"
            >
              <span className="font-bold">Back to Patient Overview</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function SnapshotLoadingOrError({ patientId, loading, error }) {
  return (
    <Layout activePage="dashboard">
      <Breadcrumb
        tupleList={[
          ['Patient Overview', `/patient/${patientId}`],
          ['Original SSI Record'],
        ]}
      />
      <div className="flex-grow pl-8 pr-8 pb-12 text-left">
        {loading && (
          <p className="text-sm text-gray-600">Loading original SSI record...</p>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SSISnapshotView() {
  const { patientId, archiveId } = useParams();
  const liveContext = useContext(PatientDataContext);
  const { patientDataModel: livePatientDataModel } = liveContext;

  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [informantsData, setInformantsData] = useState({});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const ref = doc(db, 'patients', patientId, 'ssiOverrideArchive', archiveId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (!snap.exists()) {
          setError('Original SSI record not found.');
          setArchive(null);
        } else {
          setArchive(snap.data());
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load SSI override archive:', err);
          setError('Unable to load the original SSI record.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (patientId && archiveId) {
      load();
    }

    return () => {
      cancelled = true;
    };
  }, [patientId, archiveId]);

  // Build a patient model that looks like the live one, but with the SSI
  // module replaced by the archived copy. Rating-scale / informant context
  // still comes from the live patient so the symptom panels can show the
  // same scale answers the clinician saw at the time (scales are not part
  // of the SSI module snapshot).
  const snapshotPatientDataModel = useMemo(() => {
    if (!livePatientDataModel || !archive?.previousSSIModule) return null;
    return {
      ...livePatientDataModel,
      dataModel: {
        ...livePatientDataModel.dataModel,
        '3_semiStructuredInterview': {
          ...(livePatientDataModel.dataModel?.['3_semiStructuredInterview'] || {}),
          ...archive.previousSSIModule,
        },
      },
    };
  }, [livePatientDataModel, archive]);

  // Nested context: same shape as PatientDataContext, but every write is a
  // no-op so a mis-fired blur/handler can never touch Firestore.
  const snapshotContextValue = useMemo(
    () => ({
      ...liveContext,
      patientDataModel: snapshotPatientDataModel,
      updatePatientFirestore: () => {},
      flushPendingUpdates: async () => {},
      // Keep fetch as a no-op too — reloading would pull the Lead's live SSI
      // over the archived module we just injected.
      fetchPatientData: async () => {},
    }),
    [liveContext, snapshotPatientDataModel]
  );

  // Informants are needed so the symptom panel can show rating-scale columns
  // the same way as the live proforma (read-only display only).
  useEffect(() => {
    const fetchInformantsData = async () => {
      if (!livePatientDataModel?.informants) return;

      const fetchedInformantsData = {};
      for (const informantId of Object.keys(livePatientDataModel.informants || {})) {
        try {
          const informantRef = doc(db, 'patients', informantId);
          const informantDoc = await getDoc(informantRef);
          if (informantDoc.exists()) {
            fetchedInformantsData[informantId] = informantDoc.data();
          }
        } catch (err) {
          console.error(`Error fetching informant data for ${informantId}:`, err);
        }
      }
      setInformantsData(fetchedInformantsData);
    };

    fetchInformantsData();
  }, [livePatientDataModel?.informants]);

  if (loading || error || !archive) {
    return (
      <SnapshotLoadingOrError
        patientId={patientId}
        loading={loading}
        error={error}
      />
    );
  }

  if (!snapshotPatientDataModel) {
    return (
      <SnapshotLoadingOrError
        patientId={patientId}
        loading={true}
        error={null}
      />
    );
  }

  return (
    <PatientDataContext.Provider value={snapshotContextValue}>
      <Routes>
        <Route index element={<Navigate to="symptoms" replace />} />
        <Route
          path="symptoms"
          element={
            <SnapshotSymptomsSection
              patientId={patientId}
              archiveId={archiveId}
              patientDataModel={snapshotPatientDataModel}
              informantsData={informantsData}
              archive={archive}
            />
          }
        />
        <Route
          path="domains"
          element={
            <SnapshotDomainsSection
              patientId={patientId}
              archiveId={archiveId}
              patientDataModel={snapshotPatientDataModel}
              archive={archive}
            />
          }
        />
        <Route
          path="review"
          element={
            <SnapshotReviewSection
              patientId={patientId}
              archiveId={archiveId}
              archive={archive}
            />
          }
        />
        <Route path="*" element={<Navigate to="symptoms" replace />} />
      </Routes>
    </PatientDataContext.Provider>
  );
}

export default SSISnapshotView;
